import React, {ReactNode} from 'react'
import {ClassNames} from '@emotion/core'
import {withTheme} from 'emotion-theming'

import {ThemeType} from 'types/Theme'
import {SpacingPropsType} from '../../../../../types/Layout'

type PropsType = {
  /** CSS align-content property. Sets the distribution of space between and around content items. */
  alignContent?: string
  /** CSS align-items property. Sets the align-self value on all direct children as a group. */
  alignItems?: string
  /** Content to be displayed inside the Grid */
  children: ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether this is a container Grid */
  container?: boolean
  /** CSS direction property. Sets the direction of the child Grid items. */
  direction?: string
  /** CSS justify-content property. Defines how the browser distributes space between and around child Grid items. */
  justify?: string
  /** onClick function to be passed down to the Grid div */
  onClick?: () => null
  /** Size in pixels of the combined gutter size */
  spacing?: SpacingPropsType
  /** Custom styling to be passed down to the Grid div */
  style?: Record<string, number | string>
  /** CSS flex-wrap property. Sets whether child Grid items are forced onto one line or can wrap onto multiple lines. */
  wrap?: string
  /** Grid column width of this Grid item for large desktop screens */
  desktopLg?: number | string
  /** Grid column width of this Grid item for medium desktop screens */
  desktopMd?: number | string
  /** Grid column width of this Grid item for small desktop screens */
  desktopSm?: number | string
  /** Grid column width of this Grid item for extra small desktop screens */
  desktopXs?: number | string
  /** Grid column width of this Grid item for small mobile screens */
  mobileSm?: number | string
  /** Grid column width of this Grid item for large mobile screens */
  mobileLg?: number | string
  /** The app theme. Provided by emotion */
  theme: ThemeType
}

export const Grid = ({
  alignContent,
  alignItems,
  className,
  container,
  direction,
  justify,
  spacing,
  wrap,
  mobileSm,
  mobileLg,
  desktopXs,
  desktopSm,
  desktopMd,
  desktopLg,
  children,
  onClick,
  theme,
  style
}: PropsType) => {
  const spacingValue = theme.layout.spacing[spacing]

  const combinedClassName = {
    'grid-container': container,
    'grid-item': !container,
    [`direction-mobileSm-${String(direction)}`]:
      direction !== Grid.defaultProps.direction,
    [`wrap-mobileSm-${String(wrap)}`]: wrap !== Grid.defaultProps.wrap,
    [`align-items-mobileSm-${String(alignItems)}`]:
      alignItems !== Grid.defaultProps.alignItems,
    [`align-content-mobileSm-${String(alignContent)}`]:
      alignContent !== Grid.defaultProps.alignContent,
    [`justify-mobileSm-${String(justify)}`]:
      justify !== Grid.defaultProps.justify,
    [`grid-spacing-${String(spacingValue)}`]: container && spacingValue && true,
    'grid-mobileSm': false,
    [`grid-mobileSm-${String(mobileSm)}`]: mobileSm && true,
    'grid-mobileLg': false,
    [`grid-mobileLg-${String(mobileLg)}`]: mobileLg && true,
    'grid-desktopXs': false,
    [`grid-desktopXs-${String(desktopXs)}`]: desktopXs && true,
    'grid-desktopSm': false,
    [`grid-desktopSm-${String(desktopSm)}`]: desktopSm && true,
    'grid-desktopMd': false,
    [`grid-desktopMd-${String(desktopMd)}`]: desktopMd && true,
    'grid-desktopLg': false,
    [`grid-desktopLg-${String(desktopLg)}`]: desktopLg && true
  }

  return (
    <ClassNames>
      {({cx}) => (
        <div
          className={cx(combinedClassName, className)}
          style={style}
          onClick={onClick}
        >
          {children}
        </div>
      )}
    </ClassNames>
  )
}

Grid.defaultProps = {
  alignContent: 'stretch',
  alignItems: 'stretch',
  container: false,
  direction: 'row',
  justify: 'flex-start',
  spacing: 's500',
  wrap: 'wrap'
}

export default withTheme(Grid)
